import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  // Send our HTTPOnly auth cookie + non-HTTPOnly CSRF cookie on every request.
  withCredentials: true,
});

// ---------- CSRF (double-submit cookie) ----------
const CSRF_COOKIE = 'pls_csrf_token';

const readCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : '';
};

export const getCsrfToken = () => readCookie(CSRF_COOKIE);

export const fetchMemberships = async () => {
  const { data } = await api.get('/memberships');
  return data.memberships;
};

export const fetchExtras = async () => {
  const { data } = await api.get('/extras');
  return data.extras;
};

export const createBooking = async (payload) => {
  const { data } = await api.post('/bookings', payload);
  return data;
};

export const createCheckoutSession = async (booking_id, origin_url) => {
  const { data } = await api.post('/checkout/subscription', { booking_id, origin_url });
  return data;
};

export const getCheckoutStatus = async (session_id) => {
  const { data } = await api.get(`/checkout/status/${session_id}`);
  return data;
};

export const createPaypalCheckout = async (booking_id, origin_url) => {
  const { data } = await api.post('/checkout/paypal', { booking_id, origin_url });
  return data;
};

export const getPaypalStatus = async (subscription_id) => {
  const { data } = await api.get(`/checkout/paypal/status/${subscription_id}`);
  return data;
};

export const getPaypalOrderStatus = async (order_id) => {
  const { data } = await api.get(`/checkout/paypal/order/${order_id}/status`);
  return data;
};

export const createCustomerPortal = async (email, origin_url) => {
  const { data } = await api.post('/customer-portal', { email, origin_url });
  return data;
};

// ---------- Admin ----------
const ADMIN_TOKEN_KEY = 'pls_admin_token';

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (token) => {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
};

api.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Attach CSRF header on state-changing requests when a CSRF cookie exists.
  // Backend enforces this only for cookie-authenticated admin mutations.
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options'].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

export const adminLogin = async (email, password) => {
  const { data } = await api.post('/auth/login', { email, password });
  return data;
};

export const adminLogout = async () => {
  try {
    await api.post('/auth/logout');
  } catch {
    // best-effort — cookies clear even if network fails
  }
};

export const adminMe = async () => {
  const { data } = await api.get('/auth/me');
  return data;
};

export const fetchAdminBookings = async () => {
  const { data } = await api.get('/bookings');
  return data;
};

export const updateBookingStatus = async (booking_id, status) => {
  const { data } = await api.patch(`/admin/bookings/${booking_id}/status`, { status });
  return data;
};

export const fetchAdminStats = async () => {
  const { data } = await api.get('/admin/stats');
  return data;
};
